import { useEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import { createFilterOptions } from "@mui/material";
import { FormAutocomplete } from "@covertree/ui";
import { OptionType } from "@covertree/types";
import { states } from "@covertree/consts";
import { AdditionalInterestType } from "@covertree/new-business-graphql";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

import { lenders, lendersOptions } from "./lenders";

const filter = createFilterOptions<OptionType>();

interface Props {
  disabled?: boolean;
  control: Control<AdditionalInterestFormValues>;
  fontSize?: number;
  labelFontSize?: number;
}

export interface AdditionalInterestFormValues {
  name: OptionType | string | null;
  type: AdditionalInterestType | string | null;
  streetAddress: string;
  lotUnit?: string | null;
  city: string;
  state: { label: string; value: string };
  zipCode: string;
  email?: string | null;
  accountNumber?: string | null;
  isBilling?: boolean;
}

export const toOptionType = (value?: string | null): OptionType | null => {
  const trimmed = value?.trim();
  return trimmed ? { label: trimmed, value: trimmed } : null;
};

export const cloneStateOption = (
  state: { label?: string | null; value?: string | null } | null | undefined
): { label: string; value: string } => ({
  label: state?.label ?? "",
  value: state?.value ?? "",
});

export const getStateOption = (
  stateCode?: string
): { label: string; value: string } => {
  if (!stateCode) return { label: "", value: "" };
  const match = states.find((option) => option.value === stateCode);
  return match
    ? { label: match.label, value: match.value }
    : { label: "", value: "" };
};

export function useLenderAutofill({
  lenderOption,
  setValue,
  getValues,
  skipInitialAutofill = false,
}: {
  lenderOption: OptionType | string | null | undefined;
  setValue: UseFormSetValue<AdditionalInterestFormValues>;
  getValues: UseFormGetValues<AdditionalInterestFormValues>;
  /** Start suppressed, for a form that mounts already filled with a lender. */
  skipInitialAutofill?: boolean;
}): { skipLenderAutofillRef: MutableRefObject<boolean> } {
  const skipLenderAutofillRef = useRef(skipInitialAutofill);
  const previousLenderValueRef = useRef<string | null>(null);
  const previousSelectionKindRef = useRef<"known" | "free" | null>(null);

  useEffect(() => {
    if (!lenderOption) {
      previousLenderValueRef.current = null;
      previousSelectionKindRef.current = null;
      return;
    }

    if (typeof lenderOption === "string") {
      const normalized = toOptionType(lenderOption);
      setValue("name", normalized, { shouldDirty: true, shouldValidate: true });
      previousLenderValueRef.current = null;
      return;
    }

    const normalizedSelection = toOptionType(lenderOption.value);

    if (!normalizedSelection) {
      setValue("name", null, { shouldDirty: true, shouldValidate: true });
      previousLenderValueRef.current = null;
      previousSelectionKindRef.current = null;
      return;
    }

    const selectionValue = normalizedSelection.value;
    const selectionChanged =
      normalizedSelection.value !== lenderOption.value ||
      normalizedSelection.label !== lenderOption.label;

    if (selectionChanged) {
      setValue("name", normalizedSelection, {
        shouldDirty: true,
        shouldValidate: true,
      });
      previousLenderValueRef.current = null;
      return;
    }

    const lenderDetails = lenders[selectionValue];
    const selectionKind: "known" | "free" = lenderDetails ? "known" : "free";
    const currentType = getValues("type");

    if (skipLenderAutofillRef.current) {
      previousLenderValueRef.current = selectionValue;
      previousSelectionKindRef.current = selectionKind;
      skipLenderAutofillRef.current = false;
      return;
    }

    if (
      previousLenderValueRef.current === selectionValue &&
      previousSelectionKindRef.current === selectionKind
    ) {
      return;
    }

    if (selectionKind === "known") {
      const shouldAutoSetType =
        previousSelectionKindRef.current !== "known" && !currentType;

      if (shouldAutoSetType) {
        setValue("type", AdditionalInterestType.Lienholder, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setValue("streetAddress", lenderDetails?.streetAddress ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("lotUnit", lenderDetails?.unit ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("city", lenderDetails?.city ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("state", getStateOption(lenderDetails?.state), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("zipCode", lenderDetails?.zip ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (lenderDetails?.email) {
        setValue("email", lenderDetails.email, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      previousLenderValueRef.current = selectionValue;
      previousSelectionKindRef.current = "known";
      return;
    }

    if (previousSelectionKindRef.current === "known") {
      setValue("streetAddress", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("lotUnit", "", { shouldDirty: true, shouldValidate: true });
      setValue("city", "", { shouldDirty: true, shouldValidate: true });
      setValue(
        "state",
        { label: "", value: "" },
        { shouldDirty: true, shouldValidate: true }
      );
      setValue("zipCode", "", { shouldDirty: true, shouldValidate: true });
    }

    previousLenderValueRef.current = selectionValue;
    previousSelectionKindRef.current = "free";
  }, [lenderOption, setValue, getValues]);

  return { skipLenderAutofillRef };
}

export function AdditionalInterestNameField({
  control,
  disabled = false,
  fontSize,
  labelFontSize,
}: Props) {
  return (
    <FormAutocomplete
      id="name"
      label="Name"
      options={lendersOptions}
      name="name"
      disabled={disabled}
      control={control}
      defaultValue={null}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        if (params.inputValue.trim() !== "") {
          filtered.push({
            value: params.inputValue,
            label: `Add "${params.inputValue}"`,
          });
        }

        return filtered;
      }}
      selectOnFocus
      clearOnBlur
      getOptionLabel={(option) => `${option.value}`}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
      placeholder="Start Typing..."
      fontSize={fontSize}
      labelFontSize={labelFontSize}
    />
  );
}
